import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { FormGroup, FormArray, FormControl } from '@angular/forms';
import { objectToKeyValueArray } from '@zvoove/components/utils';
import { of, merge } from 'rxjs';
import { map, switchMap, startWith, debounceTime } from 'rxjs/operators';
function hasRequiredField(abstractControl) {
  if (abstractControl.validator) {
    const validator = abstractControl.validator({});
    if (validator && validator.required) {
      return true;
    }
  }
  if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const controls = abstractControl.controls; // any because of https://github.com/microsoft/TypeScript/issues/32552
    for (const controlName in controls) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (controls[controlName]) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        if (hasRequiredField(controls[controlName])) {
          return true;
        }
      }
    }
  }
  return false;
}
/**
 * Returns the type of the control. Most of the time the type is the same as the selector.
 *
 * @param control The control class (MatSlider, MatSelect, ...)
 */
function getControlType(control) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const controlId = control.id /* MatFormFieldControl, z.B. checkbox */ || control.name; /* mat-radio-group */
  if (controlId) {
    const parts = controlId.split('-');
    if (parts[parts.length - 1].match(/[0-9]/)) {
      parts.pop();
    }
    return parts.join('-');
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (control._slider !== undefined || control._knobRadius !== undefined && control._step !== undefined) {
    return 'mat-slider';
  }
  return null;
}
let ZvFormService = /*#__PURE__*/(() => {
  class ZvFormService {
    static ɵfac = function ZvFormService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ZvFormService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ZvFormService,
      factory: ZvFormService.ɵfac,
      providedIn: 'root'
    });
  }
  return ZvFormService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BaseZvFormService extends ZvFormService {
  options = {
    debounceTime: 100,
    includeControlsDefault: false
  };
  tryDetectRequired = true;
  getControlType = getControlType;
  getControlErrors(control) {
    return this.getErrors(control, true, 'control');
  }
  getFormErrors(form, includeControls) {
    if (includeControls == null) {
      includeControls = this.options.includeControlsDefault;
    }
    return this.getErrors(form, includeControls, 'form');
  }
  /**
   * Provided to be overwritten to filter the errors.
   */
  filterErrors(errorData,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _includeControls,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _source) {
    return of(errorData);
  }
  getErrors(control, includeControls, source) {
    const update$ = this.createUpdateTrigger(control);
    return update$.pipe(map(() => this.getErrorInfo(control, includeControls)), switchMap(errorData => this.filterErrors(errorData, includeControls, source)), switchMap(errorData => this.mapDataToError(errorData)));
  }
  createUpdateTrigger(control) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return merge(control.valueChanges, control.statusChanges).pipe(startWith(null), debounceTime(this.options.debounceTime));
  }
  getErrorInfo(control, includeControls = false) {
    return this.getControlErrorInfoInternal(control, '', includeControls);
  }
  getControlErrorInfoInternal(control, controlPath, includeControls) {
    const errors = [];
    if (control instanceof FormGroup || control instanceof FormArray) {
      for (const [childName] of Object.entries(control.controls)) {
        const childControl = control.controls[childName];
        if (!(childControl instanceof FormControl) || includeControls) {
          errors.push(...this.getControlErrorInfoInternal(childControl, controlPath ? controlPath + '.' + childName : childName, includeControls));
        }
      }
    }
    if (control.errors) {
      errors.push(...objectToKeyValueArray(control.errors).map(error => this.createFormErrorData(error, control, controlPath)));
    }
    return errors;
  }
  createFormErrorData(error, control, controlPath) {
    return {
      controlPath: controlPath,
      errorKey: error.key,
      errorValue: error.value,
      isControl: control instanceof FormControl
    };
  }
}

/** @deprecated Use provideFormService instead */
let ZvFormBaseModule = /*#__PURE__*/(() => {
  class ZvFormBaseModule {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    static forRoot(formsServiceType) {
      return {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        ngModule: ZvFormBaseModule,
        providers: [{
          provide: ZvFormService,
          useClass: formsServiceType
        }]
      };
    }
    static ɵfac = function ZvFormBaseModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ZvFormBaseModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: ZvFormBaseModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
  return ZvFormBaseModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function provideFormService(formsServiceType) {
  return [{
    provide: ZvFormService,
    useClass: formsServiceType
  }];
}

// export what ./public_api exports so we can import with the lib name like this:
// import { ModuleA } from 'libname'

/**
 * Generated bundle index. Do not edit.
 */

export { BaseZvFormService, ZvFormBaseModule, ZvFormService, hasRequiredField, provideFormService };
